import type { TFunction } from "i18next"
import React, { createContext, useContext } from "react"

import { useAppContext } from "@contexts/app_context"
import useToggle from "@hooks/use_toggle"
import i18next from "@utils/localization"
import dayjs from "dayjs"
import { AiMessageStatusEnum, ChannelType } from "__generated__/graphql"

export type CallLogsFormData = {
  customerId?: string | undefined
  channelType?: ChannelType
  status?: AiMessageStatusEnum
  startDate?: Date
  endDate?: Date
  filterTrigger?: number
}

type CallLogsPageContextProps = {
  translate: TFunction<"translation", undefined>
  isLeftDrawerOpen?: boolean
  toggleIsLeftDrawerOpen?: () => void
  filters: CallLogsFormData
  setFilters: React.Dispatch<React.SetStateAction<CallLogsFormData>>
}

const defaultValues: CallLogsPageContextProps = {
  translate: i18next.t,
  filters: {},
  setFilters: () => {}
}

const CallLogsPageContext = createContext(defaultValues)

export function CallLogsPageContextProvider(props: {
  children: React.ReactNode | React.ReactNode[]
}) {
  const { translate } = useAppContext()
  const [isLeftDrawerOpen, toggleIsLeftDrawerOpen] = useToggle(true)
  const isDev =
    process.env.NODE_ENV === "development" ||
    process.env.ENV === "dev" ||
    process.env.REACT_APP_API_URL?.includes("dev")
  const [filters, setFilters] = React.useState<CallLogsFormData>({
    customerId: isDev ? "6a604c828be5a11684412fc3" : undefined,
    startDate: dayjs().subtract(1, "day").startOf("day").toDate(),
    endDate: dayjs().endOf("day").toDate()
  })

  return (
    <CallLogsPageContext.Provider
      value={{
        ...defaultValues,
        translate,
        isLeftDrawerOpen,
        toggleIsLeftDrawerOpen,
        filters,
        setFilters
      }}
    >
      {props.children}
    </CallLogsPageContext.Provider>
  )
}

export function useCallLogsPageContext() {
  const context = useContext(CallLogsPageContext)
  if (context === undefined) {
    throw new Error("CallLogsPageContext Error")
  }
  return context
}
